import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { Header2, HeaderBar, Page } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { css, useTheme } from "@emotion/react";
import Decimal from "decimal.js";
import { useEffect, useMemo, useState } from "react";
import { MdAddBox, MdLocalShipping } from "react-icons/md";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom-v5-compat";

import { Order, Package, PackageItem, TrackingStatus } from "@megaron/fulfillment-contracts";
import { useClientManager } from "@megaron/react-clients";
import { OrderDisplayFulfillmentStatus } from "@megaron/shopify";
import { newUuid, Uuid } from "@megaron/uuid";

import { BarcodeScannerBox } from "./BarcodeScannerBox";
import { PackageList } from "./packing-page/PackageList";
import { ScanDialog } from "./packing-page/ScanDialog";
import { ScheduleCourierDialog } from "./packing-page/ScheduleCourierDialog";
import { ToPackList } from "./packing-page/ToPackList";

const packageWeightLimitKg = 25;

const trackingStatusesBlockingSchedule: readonly TrackingStatus[] = [
  "stored_by_sender",
  "dispatched_by_sender",
  "collected_from_sender",
  "adopted_at_source_branch",
  "sent_from_source_branch",
  "adopted_at_sorting_center",
  "sent_from_sorting_center",
  "adopted_at_target_branch",
  "out_for_delivery",
  "out_for_delivery_to_address",
  "ready_to_pickup",
  "pickup_reminder_sent",
  "pickup_time_expired",
  "undelivered",
  "returned_to_sender",
  "delivered",
  "canceled",
  "claimed",
];

export const OrderPackingPage = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();

  const { orderId } = useParams<{ orderId: string }>();
  const orderQuery = useClientManager("fulfillment")
    .getOrder()
    .useQuery({ orderId: orderId as Uuid });
  const itemsQuery = useClientManager("docs").searchItems().useQuery({});

  const savePackageMutation = useClientManager("fulfillment").saveOrderPackage().useMutation({});
  const removePackageMutation = useClientManager("fulfillment").removeOrderPackage().useMutation({});
  const addTrackingMutation = useClientManager("fulfillment").addOrderTrackingToShopify().useMutation({});
  const scheduleCourierMutation = useClientManager("fulfillment").schedulePickupForOrder().useMutation({});

  const [packages, setPackages] = useState<Package[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [scannedCode, setScannedCode] = useState<string | null>(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [toPackItems, setToPackItems] = useState<PackageItem[]>([]);
  const [showNoPackageDialog, setShowNoPackageDialog] = useState(false);
  const [showOverweightDialog, setShowOverweightDialog] = useState(false);
  const [showScheduleCourierDialog, setShowScheduleCourierDialog] = useState(false);

  useEffect(() => {
    if (orderQuery.data?.packages) {
      setPackages(orderQuery.data.packages);
    }
  }, [orderQuery.data?.packages]);

  const docsList = useMemo(() => {
    const d: any = itemsQuery.data ?? {};
    return Array.isArray(d.items) ? d.items : Array.isArray(d.data) ? d.data : [];
  }, [itemsQuery.data]);

  const normalizeGtin = (s?: string | null) => (s ?? "").replace(/\D/g, "").replace(/^0+/, "");

  const getDisplayName = (variantId: string, fallbackName: string) => {
    const line = orderQuery.data?.items.find((i) => i.variantId === variantId);
    const code = normalizeGtin(line?.barcode ?? (line as any)?.gtin ?? null);
    if (!code) return fallbackName;
    const match = docsList.find((x: any) => normalizeGtin(x.gtin) === code);
    return match?.itemName ?? fallbackName;
  };

  useEffect(() => {
    if (!orderQuery.data) return;

    const { items, packages: existingPackages } = orderQuery.data;

    const packedCountMap = new Map<string, number>();
    const sourcePackages = packages.length ? packages : existingPackages ?? [];

    for (const pkg of sourcePackages) {
      for (const item of pkg.items) {
        packedCountMap.set(item.variantId, (packedCountMap.get(item.variantId) ?? 0) + item.quantity);
      }
    }

    const grouped = new Map<string, PackageItem>();

    for (const line of items) {
      if (line.quantity <= 0) continue;
      const packedQty = packedCountMap.get(line.variantId) ?? 0;
      const remainingQty = Math.max(0, line.quantity - packedQty);
      const key = line.barcode || line.variantId;
      const existing = grouped.get(key);
      if (existing) {
        existing.quantity += remainingQty;
      } else {
        grouped.set(key, {
          lineItemId: line.lineItemId,
          name: line.name,
          variantId: line.variantId,
          productId: line.productId,
          quantity: remainingQty,
          weight: line.weight,
          barcode: line.barcode,
          price: line.price,
          engraving: line.engraving,
        });
      }
    }

    setToPackItems(Array.from(grouped.values()));
  }, [orderQuery.data, packages]);

  const isAddPackageDisabled = (order: Order): boolean => {
    return (
      order.fulfillmentStatus === OrderDisplayFulfillmentStatus.OnHold ||
      order.fulfillmentStatus === OrderDisplayFulfillmentStatus.Fulfilled ||
      order.deliveryMethod == "palletDelivery"
    );
  };

  const persistPackage = (pkg: Package) => {
    if (!orderId) return;
    
    const previous = orderQuery.data;
    if (previous) {
      queryClient.setQueryData(orderQuery.key, {
        ...previous,
        packages: previous.packages.map((existing) => (existing.id === pkg.id ? pkg : existing)),
      });
    }
    
    savePackageMutation.mutate(
      { orderId, package: pkg },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(orderQuery.key);
        },
        onError: () => {
          if (previous) {
            queryClient.setQueryData(orderQuery.key, previous);
          }
        },
      },
    );
  };

  const handleAddPackage = () => {
    const nextId = packages.length + 1;
    const newPkg: Package = {
      id: newUuid(),
      orderId: orderId ?? "",
      name: `Paczka #${nextId}`,
      items: [],
      size: null,
      isLabelScanned: false,
      trackingNumber: null,
      shipmentId: null,
      trackingStatus: null,
      dispatchOrderId: null
    };
    const updated: Package[] = [...packages, newPkg];
    setPackages(updated);
    persistPackage(newPkg);
    if (activeId === null) {
      setActiveId(nextId);
    }
  };

  const hasPackageChanged = (prev: Package, next: Package): boolean => {
    if (prev.name !== next.name || prev.size !== next.size || prev.isLabelScanned !== next.isLabelScanned) {
      return true;
    }

    const prevItemsMap = new Map(prev.items.map((i) => [i.variantId, i.quantity]));
    const nextItemsMap = new Map(next.items.map((i) => [i.variantId, i.quantity]));

    if (prevItemsMap.size !== nextItemsMap.size) return true;

    for (const [variantId, quantity] of prevItemsMap) {
      if (nextItemsMap.get(variantId) !== quantity) return true;
    }

    for (const [variantId, quantity] of nextItemsMap) {
      if (prevItemsMap.get(variantId) !== quantity) return true;
    }

    return false;
  };

  const handleQuantityChange = (updated: Package[]) => {
    const prevById = new Map(packages.map((p) => [p.id, p]));
    const removed = packages.filter((p) => !updated.some((u) => u.id === p.id));
    const changed = updated.filter((p) => {
      const prev = prevById.get(p.id);
      return !prev || hasPackageChanged(prev, p);
    });

    setPackages(updated);

    if (!orderId) return;

    changed.forEach((pkg) => persistPackage(pkg));
    removed.forEach((pkg) =>
      removePackageMutation.mutate(
        { orderId, packageId: pkg.id },
        { onSuccess: () => queryClient.invalidateQueries(orderQuery.key) },
      ),
    );
  };

  const getPackageWeight = (pkg: Package): number => {
    const total = pkg.items.reduce((sum, item) => {
      const full = orderQuery.data?.items.find((i) => i.variantId === item.variantId);
      if (!full) return sum;
      return sum.plus(new Decimal(full.weight ?? 0).mul(item.quantity));
    }, new Decimal(0));
    return Number(total.toFixed(1));
  };

  const addItemToPackage = (pkg: Package, item: PackageItem): Package => {
    const existing = pkg.items.find((x) => x.variantId === item.variantId);
    if (existing) {
      return {
        ...pkg,
        items: pkg.items.map((x) => (x.variantId === item.variantId ? { ...x, quantity: x.quantity + 1 } : x)),
      };
    }
    return {
      ...pkg,
      items: [
        ...pkg.items,
        {
          lineItemId: item.lineItemId,
          name: item.name,
          variantId: item.variantId,
          productId: item.productId,
          quantity: 1,
          weight: item.weight,
          barcode: item.barcode,
          price: item.price,
          engraving: item.engraving,
        },
      ],
    };
  };

  const closeScanDialog = () => {
    setDialogOpen(false);
    setScannedCode(null);
  };

  const handleConfirmScan = () => {
    if (!scannedCode || activeId === null) {
      if (activeId === null) {
        closeScanDialog();
        setShowNoPackageDialog(true);
      }
      return;
    }

    const itemToPack = toPackItems.find((i) => i.barcode === scannedCode);
    if (!itemToPack || itemToPack.quantity < 1) {
      closeScanDialog();
      return;
    }

    const currentPackage = packages[activeId - 1];
    const currentWeight = getPackageWeight(currentPackage);
    const projectedWeight = new Decimal(currentWeight).plus(Number(itemToPack.weight ?? 0)).toNumber();

    if (projectedWeight > packageWeightLimitKg) {
      closeScanDialog();
      setShowOverweightDialog(true);
      return;
    }

    const updated = packages.map((pkg, i) => (i === activeId - 1 ? addItemToPackage(pkg, itemToPack) : pkg));

    setPackages(updated);
    if (orderId) {
      persistPackage(updated[activeId - 1]);
    }

    closeScanDialog();
  };

  const allPackagesHaveTracking = useMemo(
    () =>
      packages.length > 0 &&
      packages.every((p) => typeof p.trackingNumber === "string" && p.trackingNumber.trim() !== ""),
    [packages],
  );

  const allItemsPacked = useMemo(() => toPackItems.every((i) => i.quantity === 0), [toPackItems]);

  const shouldShowCourierButton = useMemo(() => {
    if (!orderQuery.data) return false;
    const order = orderQuery.data;
    if (order.packages.length === 0) return false;
    const allPacked = order.packages.every(
      (p) => p.shipmentId && (p.trackingStatus === "confirmed" || p.trackingNumber),
    );
    const hasInPostDelivery = order.deliveryMethod === "inPostCourier" || order.deliveryMethod === "inPostLocker";
    return allPacked && hasInPostDelivery;
  }, [orderQuery.data, packages]);

  const isCourierAlreadyScheduled = useMemo(() => {
    if (!orderQuery.data) return false;
    const source = orderQuery.data.packages.length ? orderQuery.data.packages : packages;
    const hasDispatchOrder = source.some((p) => p.dispatchOrderId !== null);
    const hasBlockingTrackingStatus = source.some(
      (p) => p.trackingStatus != null && trackingStatusesBlockingSchedule.includes(p.trackingStatus),
    );
    return hasDispatchOrder || hasBlockingTrackingStatus;
  }, [orderQuery.data, packages]);

  return (
    <>
      <QuerySkeleton query={orderQuery} height="58px" width="100%">
        {(order) => (
          <>
            <HeaderBar header={`WEB-${order.orderNumber}`} />

            <Page
              css={css({
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
              })}
            >
              {toPackItems.some((i) => i.quantity > 0) && order.deliveryMethod !== "palletDelivery" ? (
                <BarcodeScannerBox
                  onScan={(code) => {
                    setScannedCode(code);
                    setDialogOpen(true);
                  }}
                />
              ) : (
                <div css={{ height: "4rem" }}></div>
              )}

              {order.deliveryMethod === "palletDelivery" && <Header2>Zamowienie paletowe</Header2>}

              <ScanDialog
                scannedCode={scannedCode}
                isOpen={isDialogOpen}
                isPackageSelected={activeId !== null}
                toPackItems={toPackItems}
                projectedWeight={(() => {
                  if (!scannedCode || activeId === null) return null;
                  const item = toPackItems.find((i) => i.barcode === scannedCode);
                  if (!item) return null;
                  const current = packages[activeId - 1];
                  const currentWeight = getPackageWeight(current);
                  return Number(new Decimal(currentWeight).plus(Number((item as any).weight ?? 0)));
                })()}
                onClose={closeScanDialog}
                onConfirm={handleConfirmScan}
                getDisplayName={getDisplayName}
              />

              <div css={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginLeft: "auto" }}>
                {shouldShowCourierButton && (
                  <Button
                    onClick={() => setShowScheduleCourierDialog(true)}
                    icon={<MdLocalShipping />}
                    variant="outline"
                    isDisabled={isCourierAlreadyScheduled}
                  >
                    Zamów kuriera
                  </Button>
                )}
                {allPackagesHaveTracking && allItemsPacked && !isAddPackageDisabled(order) && (
                  <Button
                    onClick={() =>
                      addTrackingMutation.mutate(
                        { orderId: order.id },
                        { onSuccess: () => queryClient.invalidateQueries(orderQuery.key) },
                      )
                    }
                    isLoading={addTrackingMutation.isLoading}
                  >
                    Zatwierdź zamówienie
                  </Button>
                )}
              </div>

              <PackageList
                queryKey={orderQuery.key}
                order={order}
                packages={packages}
                activeId={activeId}
                orderItems={order.items}
                onSetActiveId={setActiveId}
                onQuantityChange={handleQuantityChange}
                getDisplayName={getDisplayName}
              />

              <ToPackList
                items={toPackItems}
                orderItems={order.items}
                activeId={activeId}
                onAddItem={(item) => {
                  if (activeId === null) return;

                  const currentPackage = packages[activeId - 1];
                  const currentWeight = getPackageWeight(currentPackage);
                  const projectedWeight = new Decimal(currentWeight).plus(item.weight ?? 0).toNumber();

                  if (projectedWeight > packageWeightLimitKg) {
                    setShowOverweightDialog(true);
                    return;
                  }

                  const updated = packages.map((pkg, i) => (i === activeId - 1 ? addItemToPackage(pkg, item) : pkg));

                  setPackages(updated);
                  if (orderId) {
                    persistPackage(updated[activeId - 1]);
                  }
                }}
                getDisplayName={getDisplayName}
              />

              <Button
                icon={<MdAddBox />}
                onClick={handleAddPackage}
                variant="outline"
                size="large"
                isDisabled={isAddPackageDisabled(order)}
                css={{
                  border: `1px dashed ${theme.colors.primary}`,
                  marginBottom: "18px",
                }}
              >
                Nowa paczka
              </Button>
            </Page>
          </>
        )}
      </QuerySkeleton>

      {showNoPackageDialog && (
        <Dialog header="Brak aktywnej paczki" onClose={() => setShowNoPackageDialog(false)} css={{ maxWidth: "400px" }}>
          <div css={{ padding: "1rem 0" }}>
            Zanim zeskanujesz produkt, musisz najpierw zaznaczyć paczkę, do której ma zostać dodany.
          </div>
          <div css={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setShowNoPackageDialog(false)}>OK</Button>
          </div>
        </Dialog>
      )}

      {showOverweightDialog && (
        <Dialog
          header="Przekroczony limit wagi"
          onClose={() => setShowOverweightDialog(false)}
          css={{ maxWidth: "400px" }}
        >
          <div css={{ padding: "1rem 0" }}>
            Nie możesz dodać więcej produktów do tej paczki, ponieważ przekroczyłbyś maksymalny limit wagi {packageWeightLimitKg} kg.
          </div>
          <div css={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setShowOverweightDialog(false)}>OK</Button>
          </div>
        </Dialog>
      )}

      {orderQuery.data && (
        <ScheduleCourierDialog
          isOpen={showScheduleCourierDialog}
          onClose={() => setShowScheduleCourierDialog(false)}
          onConfirm={(data) => {
            if (!orderQuery.data) return;
            scheduleCourierMutation.mutate(
              { orderId: orderQuery.data.id, ...data },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries(orderQuery.key);
                  setShowScheduleCourierDialog(false);
                },
              },
            );
          }}
        />
      )}
    </>
  );
};